import React, { useEffect, useState } from "react";
import { Alert, Box, Card, CardContent, Container, FormControl, Grid, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { toast } from "react-toastify";
import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import apis from "../../services";

const currentMonth = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const recentMonths = (count = 12) => {
  const date = new Date();
  return Array.from({ length: count }, (_, index) => {
    const month = new Date(date.getFullYear(), date.getMonth() - index, 1);
    return `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
  });
};

const formatMonth = (month) => {
  if (!month) return "";
  return new Date(`${month}-01T00:00:00`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const formatAmount = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} MXN`;

const rankingRows = (rows = []) =>
  rows.map((row, index) => ({
    ...row,
    rank: row.rank || index + 1,
    name: row.name || row.email || "Anonymous investor",
  }));

const RankingCard = ({ title, rows, valueLabel, renderValue }) => (
  <Card sx={{ height: "100%", borderRadius: 3, boxShadow: "0 8px 24px rgba(15,23,42,.08)" }}>
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg, rgba(230,14,16,.06), rgba(255,255,255,1))" }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{valueLabel}</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Investor</TableCell>
            <TableCell align="right">{valueLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length ? rows.map((row) => (
            <TableRow key={`${row.rank}-${row.email || row.name}`}>
              <TableCell>{row.rank}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
                {row.email ? <Typography variant="caption" color="text.secondary">{row.email}</Typography> : null}
              </TableCell>
              <TableCell align="right">{renderValue(row)}</TableCell>
            </TableRow>
          )) : (
            <TableRow><TableCell colSpan={3} align="center">No ranking data for this month.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const Java300Champions = () => {
  const months = recentMonths();
  const [month, setMonth] = useState(currentMonth);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await apis.getJava300Champions({ month });
        if (active) setStats(response?.data?.data || {});
      } catch (error) {
        if (active) toast.error(error?.message || "Unable to load Java 300 Champions");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [month]);

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box component="main" sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }} className="custom-scroll">
          <Header />
          <Container sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>Java 300 Champions</Typography>
                <Typography color="text.secondary">Investor and Capital rankings for {formatMonth(month)}</Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="champions-month-label">Month</InputLabel>
                <Select labelId="champions-month-label" value={month} label="Month" onChange={(event) => setMonth(event.target.value)}>
                  {months.map((option) => (
                    <MenuItem key={option} value={option}>{formatMonth(option)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {loading ? <Box className="loader-container"><div className="loader" /></Box> : (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <RankingCard title="Investor Ranking" valueLabel="Investors" rows={rankingRows(stats?.investorRanking)} renderValue={(row) => row.investors || 0} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RankingCard title="Capital Ranking" valueLabel="Capital Invested" rows={rankingRows(stats?.capitalRanking)} renderValue={(row) => formatAmount(row.capitalInvested)} />
                </Grid>
              </Grid>
            )}
            {!loading && !stats && <Alert severity="info" sx={{ mt: 3 }}>No Champions data available.</Alert>}
          </Container>
        </Box>
      </div>
    </Box>
  );
};

export default Java300Champions;
